import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useModalA11y } from '@/hooks';

interface ConfirmDialogProps {
  title?: string;
  message: string;
  checkboxLabel?: string;
  checkboxDefaultChecked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  children?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: (options: { checked: boolean }) => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  title = 'Confirm',
  message,
  checkboxLabel,
  checkboxDefaultChecked = false,
  onCheckedChange,
  children,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const checkboxId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const [checked, setChecked] = useState(checkboxDefaultChecked);
  const titleId = `${checkboxId}-title`;
  const messageId = `${checkboxId}-message`;

  useModalA11y(rootRef, onCancel);

  useEffect(() => {
    setChecked(checkboxDefaultChecked);
  }, [checkboxDefaultChecked, message]);

  return (
    <div
      ref={rootRef}
      className="form-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={messageId}
      style={{ zIndex: 1200 }}
      onClick={onCancel}
    >
      <div className="form-modal__content" onClick={(event) => event.stopPropagation()}>
        <div id={titleId} className="form-modal__title">
          {title}
        </div>
        <p
          id={messageId}
          style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}
        >
          {message}
        </p>
        {children}
        {checkboxLabel && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginTop: 'var(--space-4)', fontSize: 'var(--text-sm)' }}>
            <input
              id={checkboxId}
              type="checkbox"
              checked={checked}
              onChange={(event) => {
                setChecked(event.currentTarget.checked);
                onCheckedChange?.(event.currentTarget.checked);
              }}
            />
            {checkboxLabel}
          </label>
        )}
        <div className="form-modal__actions">
          <button type="button" className="btn btn-secondary" onClick={onCancel}>
            {cancelLabel}
          </button>
          <button type="button" className="btn btn-primary" onClick={() => onConfirm({ checked })}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
